/* eslint-disable id-length */
import Icon from "~/components/shared/Icon";
import { LogControl } from "~/components/shared/Log";
import {
  createContext,
  KeyboardEvent as ReactKeyboardEvent,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

interface LogSearchContext {
  search: string;
  position: number;
  totalMatches: number;

  setSearch: (search: string) => void;
  setPosition: (position: number) => void;
  setTotalMatches: (totalMatches: number) => void;
}

export const LogSearchContext = createContext<LogSearchContext>({
  search: "",
  position: 0,
  totalMatches: 0,
  setSearch: () => {
    return;
  },
  setPosition: () => {
    return;
  },
  setTotalMatches: () => {
    return;
  },
});

export const LogSearchProvider = ({ children }) => {
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState(0);
  const [matches, setTotalMatches] = useState(0);

  return (
    <LogSearchContext.Provider
      value={{
        search,
        setSearch,
        position,
        setPosition,
        totalMatches: matches,
        setTotalMatches,
      }}
    >
      {children}
    </LogSearchContext.Provider>
  );
};

interface Props {
  onOpen: () => void;
}

export const LogSearch = ({ onOpen }: Props) => {
  const [active, setActive] = useState(false);
  const { search, setSearch, position, totalMatches, setPosition } = useContext(LogSearchContext);
  const inputRef = useRef<HTMLInputElement>(null);

  const open = useCallback(() => {
    setActive(true);
    onOpen();
  }, []);

  const close = useCallback(() => {
    setActive(false);
    setSearch("");
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Override the default browser search with our own.
      if ((event.ctrlKey || event.metaKey) && event.key === "f") {
        // Check focus before preventing default to allow mashing Cmd+F to open native search.
        if (inputRef.current !== document.activeElement) {
          event.preventDefault();

          open();
          inputRef.current?.focus();
          inputRef.current?.select();
        } else {
          inputRef.current?.blur();
        }
      }

      // Close search if it's currently open
      if (event.key === "Escape" && active) {
        // Stop propagation to prevent the event from bubbling up and doing things like closing drawers.
        event.stopPropagation();
        close();
      }
    },
    [active],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [active]);

  /**
   * Debounce the search input handler to mimimize re-renders when typing quickly.
   */
  const handleChange = useCallback(() => {
    startTransition(() => {
      setSearch(inputRef.current?.value || "");
    });
  }, []);

  /**
   * Reset the position to the first match when the search changes.
   */
  useEffect(() => {
    if (totalMatches === 0) {
      setPosition(0);
    } else if (totalMatches > 0) {
      setPosition(1);
    }
  }, [totalMatches]);

  /**
   * Navigate to the next search result
   */
  const nextResult = useCallback(() => {
    if (!totalMatches) {
      return;
    }

    return setPosition(position === totalMatches ? 1 : position + 1);
  }, [totalMatches, position]);

  /**
   * Navigate to the previous search result
   */
  const prevResult = useCallback(() => {
    if (!totalMatches) {
      return;
    }

    return setPosition(position === 1 ? totalMatches : position - 1);
  }, [totalMatches, position]);

  /**
   * Keyboard navigation for the search results.
   */
  const handleEnter = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      // Navigate to the next match when search is focused
      if (event.key === "Enter" || (event.metaKey && event.key === "g")) {
        if (inputRef.current === document.activeElement) {
          event.preventDefault();
          nextResult();
        }
      }

      if (event.key === "Escape") {
        // Stop propagation to prevent the event from bubbling up and doing things like closing drawers.
        event.stopPropagation();
        close();
      }
    },
    [nextResult],
  );

  useEffect(() => {
    if (active) {
      inputRef.current?.focus();
    }
  }, [active]);

  return !active ? (
    <LogControl onClick={open}>
      <Icon icon="search" className="h-4 w-4" />
      Search
    </LogControl>
  ) : (
    <div
      data-active={!!search}
      className="group/search flex w-full shrink-0 overflow-hidden py-1 text-[var(--theme-text-2)]"
    >
      <label className="flex flex-auto items-center gap-1 rounded-sm border border-transparent px-1 py-0.5 focus-within:border-current focus-within:text-[var(--theme-text-1)] hover:border-[var(--theme-text-1)] group-data-[active=true]:text-[var(--theme-text-1)]">
        <Icon icon="search" className="h-4 w-4" />
        <input
          onKeyDown={handleEnter}
          ref={inputRef}
          type="text"
          name="search"
          onChange={handleChange}
          placeholder="Search"
          className="flex-auto bg-transparent outline-none placeholder:text-current"
        />

        <span className="text-xs tabular-nums text-[var(--theme-text-2)]">
          {position}/{totalMatches}
        </span>
      </label>

      <div className="ml-2 flex items-center gap-2">
        <button
          title="Previous"
          onClick={prevResult}
          disabled={totalMatches === 0}
          className="flex w-5 items-center justify-center self-stretch hover:enabled:text-[var(--theme-text-1)]"
        >
          <Icon icon="heroicons/outline/chevron-up" className="!m-0 h-3 w-3" />
        </button>
        <button
          title="Next"
          onClick={nextResult}
          disabled={totalMatches === 0}
          className="flex w-5 items-center justify-center self-stretch hover:enabled:text-[var(--theme-text-1)]"
        >
          <Icon icon="heroicons/outline/chevron-down" className="!m-0 h-3 w-3" />
        </button>
        <button
          title="Close"
          onClick={close}
          className="flex w-5 items-center justify-center self-stretch hover:enabled:text-[var(--theme-text-1)]"
        >
          <Icon icon="heroicons/20/solid/x-mark" className="!m-0 h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
